import React, { useState, useEffect, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LocalizationProvider } from './contexts/LocalizationContext'
import { AuthForm } from './components/Auth/AuthForm'
import { Dashboard } from './components/Dashboard/Dashboard'
import { SubscriptionGuard } from './components/Subscription/SubscriptionGuard'
import { Landing } from './pages/Landing'
import Subscription from './pages/Subscription'
import SubscriptionV2 from './pages/SubscriptionV2'
import SubscriptionV3 from './pages/SubscriptionV3'
import SubscriptionV4 from './pages/SubscriptionV4'
import SubscriptionV5 from './pages/SubscriptionV5'
import SubscriptionV7 from './pages/SubscriptionV7'
import SubscriptionVS from './pages/SubscriptionVS'
import SubscriptionVS1 from './pages/SubscriptionVS1'
import Terms from './pages/Terms'
import TermsXsem from './pages/TermsXsem'
import Privacy from './pages/Privacy'
import AnalyticsService from './services/analyticsService'

// Lazy load SubscriptionV6 for performance
const SubscriptionV6 = React.lazy(() => import('./pages/SubscriptionV6'))
const SubDirect = React.lazy(() => import('./pages/SubDirect'))
const SubDirectZ = React.lazy(() => import('./pages/SubDirectZ'))
const SubDirectZxsem = React.lazy(() => import('./pages/SubDirectZxsem'))
const SubDirectZxsemCrypto = React.lazy(() => import('./pages/SubDirectZxsemCrypto'))
// Lazy load SubscriptionVV to avoid bundling Stripe dependencies
const SubscriptionVV = React.lazy(() => import('./pages/SubscriptionVV'))

function SubscriptionEntryPoint() {
  const [searchParams] = useSearchParams()
  const queryMode = searchParams.get('sub') || searchParams.get('mode')
  const persistedMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const subscriptionMode = queryMode || persistedMode

  if (subscriptionMode === 'direct' || subscriptionMode === 'direct2') {
    return <SubDirectZxsemCrypto />
  }

  // Page de prod : modèle parkingboltV2 (option crypto −50%) + Stripe par défaut.
  return <SubDirectZxsemCrypto />
}

// Composant pour tracker les changements de page
function PageTracker() {
  const location = useLocation()

  useEffect(() => {
    // Tracker chaque changement de page
    AnalyticsService.trackPageView(location.pathname, document.title)

    // Persister le mode d'abonnement lorsqu'il est présent dans l'URL
    const params = new URLSearchParams(location.search)
    const modeFromUrl = params.get('sub') || params.get('mode')
    if (modeFromUrl === 'direct' || modeFromUrl === 'direct2') {
      sessionStorage.setItem('preferred_subscription_mode', modeFromUrl)
      localStorage.setItem('preferred_subscription_mode', modeFromUrl)
    }
  }, [location])

  return null
}

// Component for protected routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-paybyphone-light flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paybyphone-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}

// Auth page component
function AuthPage() {
  const { user } = useAuth()
  const location = useLocation() as any
  const [searchParams] = useSearchParams()
  const authModeParam = searchParams.get('auth') || searchParams.get('mode')
  const initialMode = authModeParam === 'signup' ? 'signup' : 'signin'
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(initialMode)

  // Propager le paramètre `payment` vers /subscription pour garantir le bon provider.
  // Exemple: /auth?mode=signup&payment=stripe -> /subscription?payment=stripe
  const paymentParamRaw = searchParams.get('payment')
  const paymentParam = paymentParamRaw === 'stripe' || paymentParamRaw === 'systempay' ? paymentParamRaw : null
  const modeParam = searchParams.get('sub') || searchParams.get('mode')
  const sessionMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const directModeFromSession = sessionMode === 'direct' || sessionMode === 'direct2'
  const directModeParam =
    modeParam === 'direct' || modeParam === 'direct2'
      ? modeParam
      : directModeFromSession
        ? sessionMode
        : null

  const stateRedirect = location?.state?.from as string | undefined
  // Si on force un mode/provider depuis la campagne, on priorise toujours /subscription
  // (sinon un state.from='/dashboard' peut faire perdre mode=direct)
  const forceSubscriptionRedirect = directModeParam === 'direct' || !!paymentParam
  const redirectTo = directModeParam === 'direct2'
    ? '/dashboard'
    : forceSubscriptionRedirect
      ? '/subscription'
      : (stateRedirect || '/dashboard')

  const resolvedRedirectTo = React.useMemo(() => {
    // On ne force que pour les pages de paiement/abonnement
    const isSubscriptionTarget = redirectTo.startsWith('/subscription')

    if (!isSubscriptionTarget || (!paymentParam && !directModeParam)) return redirectTo

    const [path, queryString = ''] = redirectTo.split('?')
    const params = new URLSearchParams(queryString)

    if (paymentParam && !params.has('payment')) {
      params.set('payment', paymentParam)
    }

    if (directModeParam && !params.has('sub')) {
      params.set('sub', directModeParam)
    }

    const nextQuery = params.toString()
    return nextQuery ? `${path}?${nextQuery}` : path
  }, [redirectTo, paymentParam, directModeParam])

  // If user is already logged in, redirect to the page they originally requested
  if (user) {
    return <Navigate to={resolvedRedirectTo} replace />
  }

  return (
    <AuthForm
      mode={authMode}
      onToggleMode={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}
    />
  )
}

function App() {
  useEffect(() => {
    // Initialiser Google Analytics au démarrage de l'application
    AnalyticsService.init()
  }, [])

  return (
    <LocalizationProvider>
      <AuthProvider>
        <Router>
          <PageTracker />
          <Suspense fallback={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          }>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/terms-xsem" element={<TermsXsem />} />
              <Route path="/privacy" element={<Privacy />} />
              
              {/* Protected routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <SubscriptionGuard>
                      <Dashboard />
                    </SubscriptionGuard>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <SubscriptionEntryPoint />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/subscription-v1" 
                element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/subscription-v2" 
                element={
                  <ProtectedRoute>
                    <SubscriptionV2 />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/subscription-v3"
                element={
                  <ProtectedRoute>
                    <SubscriptionV3 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-v4"
                element={
                  <ProtectedRoute>
                    <SubscriptionV4 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-v5"
                element={
                  <ProtectedRoute>
                    <SubscriptionV5 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-v6"
                element={
                  <ProtectedRoute>
                    <SubscriptionV6 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-v7"
                element={
                  <ProtectedRoute>
                    <SubscriptionV7 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-vs"
                element={
                  <ProtectedRoute>
                    <SubscriptionVS />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-vs1"
                element={
                  <ProtectedRoute>
                    <SubscriptionVS1 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-vv"
                element={
                  <ProtectedRoute>
                    <SubscriptionVV />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subdirectz"
                element={
                  <ProtectedRoute>
                    <SubDirectZ />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subdirectzxsem"
                element={
                  <ProtectedRoute>
                    <SubDirectZxsem />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription-crypto"
                element={
                  <ProtectedRoute>
                    <SubDirectZxsemCrypto />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </LocalizationProvider>
  )
}

export default App
